import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { Link, RouteComponentProps, withRouter } from "react-router-dom";
import { type Dispatch, type UnknownAction } from "redux";
import { CircleCheck, Lock, TriangleAlert, X } from "lucide-react";

import Loading from "../../components/Loading";
import { Badge } from "@/components/ui/badge";
import { WFPButton } from "@/components/ui/button";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Card, CardContent } from "@/components/ui/card";
import { Combobox } from "@/components/ui/combobox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatFriendlyDateTime } from "@/utils/relative-time";
import { displayErrorMessage, isEmpty } from "../../utils";
import { isUserExternal, isUserWfpManager } from "../../utils/authentication";
import {
  acceptBid,
  acceptBidReset,
  bidComparison,
  bidComparisonReset,
  listPaymentMethods,
  listPaymentMethodsReset,
} from "../../actions";
import {
  BID_REQUEST_STATUS,
  INVITATION_RESPONSE_STATUS,
  type BidComparison,
  type SchoolBid,
} from "../BidRequests/types";
import type { PaymentMethod } from "../PaymentMethods/types";
import {
  buildDeliveryViews,
  buildUnitCodeByUnitId,
  formatBidRequestDate,
  formatMoney,
  formatQuantity,
  getBiddingPeriodLabel,
  getDaysLeft,
  getFulfilmentChip,
  getStatusChip,
  getStockProgress,
  getWinningBid,
  isAwaitingStockOrAcceptance,
  isSettled,
} from "../BidRequests/utils";
import { AcceptBidDialog } from "./components/AcceptBidDialog";
import { BidComparisonCard } from "./components/BidComparisonCard";
import { DeliveriesTab } from "./components/DeliveriesTab";
import { OffersTab } from "./components/OffersTab";
import { PaymentsTab } from "./components/PaymentsTab";
import { ViewBidDialog } from "./components/ViewBidDialog";

const RESPONSE_FILTER = {
  all: "ALL",
  awaiting: "AWAITING",
  received: "RECEIVED",
} as const;

type ResponseFilter = (typeof RESPONSE_FILTER)[keyof typeof RESPONSE_FILTER];

interface StateProps {
  comparison: BidComparison | undefined;
  isFetching: boolean;
  errorMessage: string;
  isAccepting: boolean;
  acceptError: string;
  hasAcceptError: boolean;
  paymentMethods: PaymentMethod[];
}

interface DispatchProps {
  loadComparison: (id: string) => void;
  resetComparison: () => void;
  acceptBid: (id: string, bidId: number) => Promise<unknown>;
  resetAccept: () => void;
  loadPaymentMethods: () => void;
  resetPaymentMethods: () => void;
}

type BidRequestDetailsProps = StateProps &
  DispatchProps &
  RouteComponentProps<{ id: string }> & { readOnly?: boolean };

export const BidRequestDetails = ({
  comparison,
  isFetching,
  errorMessage,
  isAccepting,
  acceptError,
  hasAcceptError,
  paymentMethods,
  loadComparison,
  resetComparison,
  acceptBid,
  resetAccept,
  loadPaymentMethods,
  resetPaymentMethods,
  match,
  readOnly = false,
}: BidRequestDetailsProps) => {
  const { t, i18n } = useTranslation();
  const { id } = match.params;

  const [viewBid, setViewBid] = useState<SchoolBid | null>(null);
  const [bidToAccept, setBidToAccept] = useState<SchoolBid | null>(null);
  const [acceptFailed, setAcceptFailed] = useState(false);
  const [search, setSearch] = useState("");
  const [responseFilter, setResponseFilter] = useState<ResponseFilter>(RESPONSE_FILTER.all);

  useEffect(() => {
    loadComparison(id);
    loadPaymentMethods();
    return () => {
      resetComparison();
      resetAccept();
      resetPaymentMethods();
    };
  }, [id, loadComparison, resetComparison, resetAccept, loadPaymentMethods, resetPaymentMethods]);

  if (isFetching || (!comparison && !errorMessage)) {
    return <Loading isVisible />;
  }

  if (errorMessage || !comparison || isEmpty(comparison)) {
    return (
      <main className="flex flex-col gap-6 py-4 px-6 sm:py-6 sm:px-8 md:py-8 md:px-10">
        {displayErrorMessage(errorMessage || t("bidRequests.detail.notFound"))}
      </main>
    );
  }

  const isBlind = comparison.is_blind;
  const isAccepted = comparison.status === BID_REQUEST_STATUS.acceptedBid;
  const canAccept = !readOnly && comparison.status === BID_REQUEST_STATUS.biddingComplete;
  const submittedCount = comparison.submitted_count;
  const invitedCount = comparison.invited_count;
  const daysLeft = getDaysLeft(comparison.posting_end_date);
  const winningBid = isAccepted ? getWinningBid(comparison.bids) : undefined;
  const acceptDialogError =
    hasAcceptError || acceptFailed ? acceptError || t("bidRequests.detail.acceptDialog.error") : "";

  const { settlement } = comparison;
  const aggregatorName = winningBid?.aggregator_name ?? "";
  const unitCodeByUnitId = buildUnitCodeByUnitId(comparison.items);
  const deliveryViews = settlement ? buildDeliveryViews(settlement, unitCodeByUnitId) : [];
  const anyDelivered = settlement
    ? settlement.commodities.some(commodity => Number(commodity.delivered_quantity) > 0)
    : false;
  const paymentsCount = settlement?.payments.length ?? 0;
  const awaitingStock = isAwaitingStockOrAcceptance(settlement?.fulfilment_status);
  const userProfile = localStorage.getItem("user_profile");
  const showFarmland = isUserWfpManager(userProfile) || isUserExternal(userProfile);

  const statusChip = getStatusChip(comparison.status, t);
  const fulfilmentChip = settlement ? getFulfilmentChip(settlement.fulfilment_status, t) : null;

  const bidsReceivedLabel = t("bidRequests.card.bidsReceived", {
    submitted: submittedCount,
    total: invitedCount,
  });

  const matchesSearch = (name: string) => !search || name === search;

  const aggregatorOptions = [
    { value: "", label: t("bidRequests.detail.search.allAggregators") },
    ...Array.from(new Set(comparison.invitations.map(invitation => invitation.aggregator_name)))
      .sort((a, b) => a.localeCompare(b))
      .map(name => ({ value: name, label: name })),
  ];

  const filteredInvitations = comparison.invitations.filter(invitation => {
    if (!matchesSearch(invitation.aggregator_name)) {
      return false;
    }
    const hasSubmitted = invitation.response_status === INVITATION_RESPONSE_STATUS.submitted;
    if (responseFilter === RESPONSE_FILTER.received) {
      return hasSubmitted;
    }
    if (responseFilter === RESPONSE_FILTER.awaiting) {
      return !hasSubmitted;
    }
    return true;
  });

  const filteredBids = comparison.bids.filter(bid => {
    if (!matchesSearch(bid.aggregator_name)) {
      return false;
    }
    return responseFilter !== RESPONSE_FILTER.awaiting;
  });

  const handleRequestAccept = (bid: SchoolBid) => {
    setViewBid(null);
    resetAccept();
    setAcceptFailed(false);
    setBidToAccept(bid);
  };

  const handleCancelAccept = () => {
    setBidToAccept(null);
    setAcceptFailed(false);
    resetAccept();
  };

  const handleConfirmAccept = (bid: SchoolBid) => {
    setAcceptFailed(false);
    acceptBid(id, bid.id).then(result => {
      if (result) {
        setBidToAccept(null);
        loadComparison(id);
      } else {
        setAcceptFailed(true);
      }
    });
  };

  return (
    <main className="flex flex-col gap-6 py-4 px-6 sm:py-6 sm:px-8 md:py-8 md:px-10">
      <div className="flex flex-col gap-2">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to={readOnly ? "/wfpManager/bidRequests" : "/representative/bidRequests"}>
                  {t("bidRequests.title")}
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>
                {t("bidRequests.card.reference", { reference: comparison.buyer_reference })}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="font-semibold text-2xl">
            {t("bidRequests.card.reference", { reference: comparison.buyer_reference })}
          </h1>
          <Badge variant={statusChip.variant}>{statusChip.label}</Badge>
          {fulfilmentChip && (
            <span className="flex items-center gap-2 text-muted-foreground text-sm">
              {t("bidRequests.card.fulfilmentLabel")}
              <Badge variant={fulfilmentChip.variant}>{fulfilmentChip.label}</Badge>
            </span>
          )}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <Tabs defaultValue="bids">
          <TabsList className="w-full justify-start">
            <TabsTrigger value="bids">
              {t("bidRequests.detail.bidsTab", { submitted: submittedCount, total: invitedCount })}
            </TabsTrigger>
            <TabsTrigger value="offers" disabled={!winningBid}>
              {t("bidRequests.detail.offersTab")}
            </TabsTrigger>
            <TabsTrigger value="deliveries" disabled={!settlement}>
              {t("bidRequests.detail.deliveriesTab", { count: deliveryViews.length })}
            </TabsTrigger>
            <TabsTrigger value="payments" disabled={!settlement}>
              {t("bidRequests.detail.paymentsTab", { count: paymentsCount })}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="bids" className="mt-4 flex flex-col gap-4">
            {isBlind && (
              <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/40 p-4">
                <Lock className="mt-0.5 size-4 text-muted-foreground" />
                <div className="flex flex-col gap-1">
                  <p className="font-medium text-sm">{bidsReceivedLabel}</p>
                  <p className="text-muted-foreground text-sm">
                    {t("bidRequests.detail.blindBanner", {
                      date: formatBidRequestDate(comparison.posting_end_date),
                    })}
                  </p>
                </div>
              </div>
            )}

            <Card className="gap-0 p-4">
              <div className="flex flex-wrap items-center gap-3">
                <Combobox
                  id="aggregator-filter"
                  className="w-full sm:w-64"
                  options={aggregatorOptions}
                  value={search}
                  onChange={setSearch}
                  placeholder={t("bidRequests.detail.search.placeholder")}
                  searchPlaceholder={t("bidRequests.detail.search.placeholder")}
                  emptyText={t("bidRequests.detail.search.noAggregators")}
                />
                <Select
                  value={responseFilter}
                  onValueChange={value => setResponseFilter(value as ResponseFilter)}
                >
                  <SelectTrigger
                    className="w-full sm:w-48"
                    aria-label={t("bidRequests.detail.search.responseLabel")}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={RESPONSE_FILTER.all}>
                      {t("bidRequests.detail.search.allResponses")}
                    </SelectItem>
                    <SelectItem value={RESPONSE_FILTER.awaiting}>
                      {t("bidRequests.detail.awaitingBid")}
                    </SelectItem>
                    <SelectItem value={RESPONSE_FILTER.received}>
                      {t("bidRequests.detail.bidReceived")}
                    </SelectItem>
                  </SelectContent>
                </Select>
                {(search.trim() !== "" || responseFilter !== RESPONSE_FILTER.all) && (
                  <WFPButton
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      setSearch("");
                      setResponseFilter(RESPONSE_FILTER.all);
                    }}
                  >
                    <X />
                    {t("filters.clearAll")}
                  </WFPButton>
                )}
              </div>
            </Card>

            {canAccept && (
              <div className="flex items-start gap-3 rounded-lg border border-info-300 bg-info-100 p-4">
                <CircleCheck className="mt-0.5 size-4 text-info-700" />
                <div className="flex flex-col gap-1">
                  <p className="font-medium text-sm">{bidsReceivedLabel}</p>
                  <p className="text-muted-foreground text-sm">
                    {t("bidRequests.detail.recommendedBanner")}
                  </p>
                </div>
              </div>
            )}

            {isBlind ? (
              filteredInvitations.length === 0 ? (
                <p className="py-10 text-center text-muted-foreground text-sm">
                  {t("bidRequests.detail.search.noMatches")}
                </p>
              ) : (
                filteredInvitations.map(invitation => {
                  const hasSubmitted =
                    invitation.response_status === INVITATION_RESPONSE_STATUS.submitted;
                  const respondedAt = formatFriendlyDateTime(invitation.responded_at, t);
                  return (
                    <Card key={invitation.id} className="gap-0 py-0">
                      <CardContent className="flex flex-col gap-2 p-4">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          {hasSubmitted ? (
                            <Badge variant="success">{t("bidRequests.detail.bidReceived")}</Badge>
                          ) : (
                            <Badge variant="warning">{t("bidRequests.detail.awaitingBid")}</Badge>
                          )}
                          {hasSubmitted && respondedAt && (
                            <span className="text-muted-foreground text-sm">{respondedAt}</span>
                          )}
                        </div>
                        <p className="font-semibold">{invitation.aggregator_name}</p>
                        {hasSubmitted && (
                          <span className="flex items-center gap-1 text-muted-foreground text-sm">
                            {t("bidRequests.detail.maskedPrice", {
                              currency:
                                comparison.currency?.symbol ?? comparison.currency?.code ?? "",
                            })}
                            <Lock className="size-3.5" />
                          </span>
                        )}
                      </CardContent>
                    </Card>
                  );
                })
              )
            ) : filteredBids.length === 0 ? (
              <p className="py-10 text-center text-muted-foreground text-sm">
                {t("bidRequests.detail.search.noMatches")}
              </p>
            ) : (
              filteredBids.map(bid => (
                <BidComparisonCard key={bid.id} bid={bid} onViewBid={setViewBid} />
              ))
            )}
          </TabsContent>

          {winningBid && (
            <TabsContent value="offers" className="mt-4">
              <OffersTab
                bid={winningBid}
                demand={comparison.generated_demand}
                settlement={settlement}
                unitCodeByUnitId={unitCodeByUnitId}
                showFarmland={showFarmland}
              />
            </TabsContent>
          )}

          {settlement && (
            <TabsContent value="deliveries" className="mt-4">
              <DeliveriesTab
                deliveries={deliveryViews}
                aggregatorName={aggregatorName}
                awaitingStock={awaitingStock}
              />
            </TabsContent>
          )}

          {settlement && (
            <TabsContent value="payments" className="mt-4">
              <PaymentsTab
                settlement={settlement}
                currency={comparison.currency}
                aggregatorName={aggregatorName}
                paymentMethods={paymentMethods}
                language={i18n.language}
              />
            </TabsContent>
          )}
        </Tabs>

        <Card className="h-fit">
          <CardContent className="flex flex-col gap-4">
            {isAccepted && winningBid && (
              <div className="flex items-start gap-3 rounded-lg border border-success-300 bg-success-100 p-4">
                <CircleCheck className="mt-0.5 size-4 text-success-700" />
                <div className="flex flex-col gap-0.5">
                  <p className="font-semibold text-sm">
                    {t("bidRequests.detail.acceptedBox.title")}
                  </p>
                  <p className="text-muted-foreground text-sm">
                    {t("bidRequests.detail.acceptedBox.from", {
                      aggregator: winningBid.aggregator_name,
                    })}
                  </p>
                </div>
              </div>
            )}

            <div className="flex flex-col gap-2">
              <h2 className="font-semibold">{t("bidRequests.detail.requestedCommodities")}</h2>
              <ul className="flex flex-col gap-1 text-sm">
                {settlement
                  ? settlement.commodities.map(commodity => {
                      const unit = unitCodeByUnitId[commodity.unit_of_measure_id];
                      const remaining = Number(commodity.remaining_quantity);
                      const showRemaining = anyDelivered && remaining > 0;
                      const fullyDelivered = anyDelivered && remaining === 0;
                      const stock = awaitingStock ? getStockProgress(commodity, unit) : null;
                      return (
                        <li key={commodity.child_offer_id} className="flex items-center gap-2">
                          {(fullyDelivered || stock?.complete) && (
                            <CircleCheck className="size-4 shrink-0 text-success-700" />
                          )}
                          {showRemaining && (
                            <TriangleAlert className="size-4 shrink-0 text-warning-700" />
                          )}
                          <span>
                            {commodity.commodity_name},{" "}
                            {formatQuantity(commodity.agreed_quantity, unit)}
                          </span>
                          {showRemaining && (
                            <span className="text-warning-700">
                              {t("bidRequests.detail.remaining", {
                                quantity: formatQuantity(remaining, unit),
                              })}
                            </span>
                          )}
                          {stock && !stock.complete && (
                            <span className="text-muted-foreground">
                              {t("bidRequests.detail.stockAssigned", {
                                linked: stock.linked,
                                agreed: stock.agreed,
                              })}
                            </span>
                          )}
                        </li>
                      );
                    })
                  : comparison.items.map(item => (
                      <li key={item.id}>
                        {item.commodity_name},{" "}
                        {formatQuantity(item.quantity, item.unit_of_measure_code)}
                      </li>
                    ))}
              </ul>
            </div>

            {isAccepted && (settlement || winningBid) && (
              <>
                <Separator />
                <div className="flex flex-col gap-1">
                  <h2 className="font-semibold">{t("bidRequests.detail.price")}</h2>
                  {settlement ? (
                    isSettled(settlement) ? (
                      <p className="flex items-center gap-2 text-sm">
                        <CircleCheck className="size-4 shrink-0 text-success-700" />
                        {formatMoney(comparison.currency, settlement.agreed_total)}
                      </p>
                    ) : (
                      <p className="flex items-center gap-2 text-sm">
                        <TriangleAlert className="size-4 shrink-0 text-warning-700" />
                        <span>{formatMoney(comparison.currency, settlement.agreed_total)}</span>
                        <span className="text-warning-700">
                          {t("bidRequests.detail.priceOutstanding", {
                            amount: formatMoney(comparison.currency, settlement.outstanding),
                          })}
                        </span>
                      </p>
                    )
                  ) : (
                    winningBid && (
                      <p className="text-sm">
                        {formatMoney(winningBid.currency, winningBid.total)}
                      </p>
                    )
                  )}
                  {winningBid && Number(winningBid.transport_cost) > 0 && (
                    <p className="flex justify-between text-muted-foreground text-sm">
                      <span>{t("bidRequests.detail.transport")}</span>
                      <span>{formatMoney(winningBid.currency, winningBid.transport_cost)}</span>
                    </p>
                  )}
                </div>
              </>
            )}

            <Separator />

            <div className="flex flex-col gap-1">
              <h2 className="font-semibold">{t("bidRequests.detail.delivery")}</h2>
              <p className="text-sm">{formatBidRequestDate(comparison.delivery_date)}</p>
              <p className="text-muted-foreground text-sm">{comparison.delivery_location}</p>
            </div>

            <Separator />

            <div className="flex flex-col gap-1">
              <h2 className="font-semibold">{t("bidRequests.detail.biddingPeriod")}</h2>
              <p className="text-sm">
                {getBiddingPeriodLabel(comparison)}
                {isBlind && (
                  <span className="ml-2 text-warning-700">
                    {t("bidRequests.card.daysLeft", { count: daysLeft })}
                  </span>
                )}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      <ViewBidDialog
        bid={viewBid}
        canAccept={canAccept}
        onClose={() => setViewBid(null)}
        onAccept={handleRequestAccept}
      />
      {!readOnly && (
        <AcceptBidDialog
          bid={bidToAccept}
          isAccepting={isAccepting}
          errorMessage={acceptDialogError}
          onCancel={handleCancelAccept}
          onConfirm={handleConfirmAccept}
        />
      )}
    </main>
  );
};

interface RequestState<T> {
  isFetching: boolean;
  errorMessage: string;
  data: T;
}

interface AcceptState {
  isLoading: boolean;
  error: unknown;
}

interface RootState {
  bidComparison: RequestState<BidComparison | Record<string, never>>;
  acceptBid: AcceptState;
  listPaymentMethods: RequestState<{ results?: PaymentMethod[] }>;
}

const extractAcceptError = (error: unknown): string => {
  if (isEmpty(error) || typeof error !== "object" || error === null) {
    return "";
  }
  const bidErrors = (error as { bid?: unknown }).bid;
  if (Array.isArray(bidErrors) && bidErrors.length > 0) {
    return String(bidErrors[0]);
  }
  return "";
};

export const mapStateToProps = (state: RootState): StateProps => ({
  comparison: isEmpty(state.bidComparison.data)
    ? undefined
    : (state.bidComparison.data as BidComparison),
  isFetching: state.bidComparison.isFetching,
  errorMessage: state.bidComparison.errorMessage,
  isAccepting: state.acceptBid.isLoading,
  acceptError: extractAcceptError(state.acceptBid.error),
  hasAcceptError: !isEmpty(state.acceptBid.error),
  paymentMethods: state.listPaymentMethods.data.results ?? [],
});

type AppDispatch = Dispatch<UnknownAction>;

export const mapDispatchToProps = (dispatch: AppDispatch): DispatchProps => ({
  loadComparison: id => dispatch(bidComparison(id, "/bids/") as unknown as UnknownAction),
  resetComparison: () => dispatch(bidComparisonReset() as unknown as UnknownAction),
  acceptBid: (id, bidId) =>
    dispatch(
      acceptBid({ bid: bidId }, `${id}/accept/`) as unknown as UnknownAction
    ) as unknown as Promise<unknown>,
  resetAccept: () => dispatch(acceptBidReset() as unknown as UnknownAction),
  loadPaymentMethods: () => dispatch(listPaymentMethods() as unknown as UnknownAction),
  resetPaymentMethods: () => dispatch(listPaymentMethodsReset() as unknown as UnknownAction),
});

export const ConnectedBidRequestDetails = connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(BidRequestDetails));

export const WfpManagerBidRequestDetails = () => <ConnectedBidRequestDetails readOnly />;
